import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import config from '@/utility/config';
import { checkIfWebIsInFrame, openInNewTab } from '@/utility/helpers';
import { t } from '@/utility/localization';
import {
  handleLogoutClickFromProductDemo,
  isSessionInSandboxEnv
} from '@/utility/productDemoUtils';
import sessionStorageService from '@/utility/sessionStorageService';

import { useAuthContext } from '@/contexts/AuthContext';
import { useCommunityPortalContext } from '@/contexts/CommunityPortalContext';
import { useIntercom } from '@/contexts/IntercomContext/CommonIntercomContext';
import { useLibrarySetupContext } from '@/contexts/LibrarySetupContext';
import { STATUS } from '@/contexts/LibrarySetupContext/constants';
import { useWindowWidthContext } from '@/contexts/WindowWidthContext';

import Card from '@/components/common/Card';
import LoadingCircle from '@/components/common/LoadingCircle';
import { generateNextImagePath } from '@/components/common/NextImage/NextImage';
import PageMetaTags from '@/components/common/PageMetaTags';
import {
  checkIfNewImageOptimization,
  generateImagePath
} from '@/components/common/ProgressiveImage';
import AuthNavBar from '@/components/features/Navbar/AuthNavbar';

import PortalSidebar from '@/pages/portal/components/PortalSidebar';
import getConstants from '@/pages/portal/constants';

import { isActivePathName, shouldHideDrawerNav } from '../helpers';
import { useAnnouncementsModal } from '../hooks/useAnnouncementsModal';
import { COMM_MONEY_PAGE_VIEW_MAP_SESSION_KEY } from '../money/MoneyPageContent';
import DrawerNavbar from './DrawerNavbar';

// Dynamic imports
const WaConnectionModals = dynamic(
  () => import('@/components/features/WaConnectionModals')
);
const NoCommunityModal = dynamic(
  () =>
    import('@/pages/login/components/NoCommunityModal/NoCommunityModal')
);
const AnnouncementModal = dynamic(
  () => import('@/components/features/AnnouncementModal')
);
const CampaignAnnouncementModals = dynamic(
  () =>
    import('@/features/CmCampaigns/components/CampaignAnnouncementModals')
);
const EditWelcomeMessageSideDrawer = dynamic(
  () =>
    import('./EditWelcomeMessageSideDrawer/EditWelcomeMessageSideDrawer')
);
const GetStartedDismissalModal = dynamic(
  () => import('../get-started/components/GetStartedDismissalModal')
);
const AnnouncementBar = dynamic(
  () => import('@/components/common/AnnouncementBar')
);
const KycRequiredBanner = dynamic(
  () => import('@/components/common/KycRequiredBanner')
);

const PortalLayout = ({ children }) => {
  const className = 'c-PortalLayout';
  const {
    LIBRARY_PAGE,
    MEMBERS_PAGE,
    MONEY_PAGE,
    SETTING_PAGE,
    groupedNavItems,
    GETTING_STARTED_PAGE,
    NASIO_MANAGER_PORTAL_TEXT
  } = getConstants();
  const { shutdown } = useIntercom();
  const { isGtEqLg } = useWindowWidthContext();
  const pathName = usePathname();
  const { user, isDemoUser, logout } = useAuthContext();

  const [isAnnouncementBannerOpened, setIsAnnouncementBannerOpened] =
    useState(false);
  const [isNoCommunityModalOpen, setIsNoCommunityModalOpen] =
    useState(false);
  const [
    isGetStartedDismissalModalOpen,
    setIsGetStartedDismissalModalOpen
  ] = useState(false);

  const {
    allCommunities,
    loadingCommunities,
    activeCommunity,
    communitiesErr,
    setActiveCommunity,
    showPayoutNudge,
    showConnectWhatsAppModals,
    loadingCommunitiesAsync,
    handleCloseWhatsappConnectModals,
    membershipPendingApplicationCount,
    taskProgressPerc,
    isWelcomeMessageEditorOpen,
    setIsWelcomeMessageEditorOpen
    // showFeePreferencePopup,
    // updateNasioFeePreference,
    // closeFeePreferencePopup,
    // isSettingFeePreference
  } = useCommunityPortalContext();
  const isDemoCommunityActive = activeCommunity?.isDemo;

  const { isAnnouncementModalOpen, modalProps } = useAnnouncementsModal({
    activeCommunity
  });

  //Check if user have any community
  useEffect(() => {
    if (
      !loadingCommunities &&
      !loadingCommunitiesAsync &&
      allCommunities?.length === 0
    ) {
      setIsNoCommunityModalOpen(true);
    }
  }, [
    allCommunities?.length,
    loadingCommunities,
    loadingCommunitiesAsync
  ]);

  const isInFrame = checkIfWebIsInFrame();

  const showDrawerNav =
    !isGtEqLg && !shouldHideDrawerNav(pathName) && !isInFrame;

  const libraryPageState = useLibrarySetupContext();
  const libraryUploadStatus = libraryPageState?.status ?? false;

  const communityNavigationList = allCommunities?.map((community) => {
    const { title, thumbnailImgData, _id } = community;
    return {
      title,
      thumbnailImgData,
      onClick: () => {
        setActiveCommunity(community);
      },
      isActive: activeCommunity?._id === _id
    };
  });

  const getNavigationItems = () => {
    // Announcement = home
    const mappedNavItems = groupedNavItems?.map((navItems) => ({
      ...navItems,
      items: navItems.items.map((item) => {
        return {
          ...item,
          isActive: isActivePathName({ pathName, route: item }),
          inProgress:
            item.name === LIBRARY_PAGE.name &&
            libraryUploadStatus === STATUS.UPLOADING,
          show: (() => {
            if (item === GETTING_STARTED_PAGE) {
              if (
                activeCommunity?.config?.hideGetStarted ||
                activeCommunity?.taskMetaData
                  ?.tasksCompletionPercentage === 100
              ) {
                return false;
              }

              return taskProgressPerc !== 100;
            }

            return true;
          })(),
          showProgressRing: item.name === GETTING_STARTED_PAGE.name,
          onDismissalCtaClick:
            item.name === GETTING_STARTED_PAGE.name
              ? (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsGetStartedDismissalModalOpen(true);
                }
              : null,
          progress:
            item.name === GETTING_STARTED_PAGE.name ? taskProgressPerc : 0,
          isActionable: ((type) => {
            if (loadingCommunities) return false;

            switch (type) {
              case MONEY_PAGE.name:
                // find for which all communities has the money page been viewed in current session.
                const commToMoneyPageViewMapStr =
                  sessionStorageService.getItem(
                    COMM_MONEY_PAGE_VIEW_MAP_SESSION_KEY
                  );
                const commToMoneyPageViewMapInSession =
                  commToMoneyPageViewMapStr
                    ? JSON.parse(commToMoneyPageViewMapStr)
                    : {};
                const communityId = activeCommunity?._id;

                // this checks if user has view money page for this community in current session.
                if (commToMoneyPageViewMapInSession[communityId])
                  return false;

                return !activeCommunity?.hasViewedMoneyPage;
              case SETTING_PAGE.name:
                return showPayoutNudge;
              case MEMBERS_PAGE.name:
                return membershipPendingApplicationCount > 0;
              default:
                return false;
            }
          })(item.name)
        };
      })
    }));
    return mappedNavItems;
  };

  function handleLogout() {
    shutdown();

    if (isSessionInSandboxEnv() && isDemoCommunityActive) {
      handleLogoutClickFromProductDemo();
      return;
    }

    logout(true, true, '/');
  }

  const selectActiveCommunity = useCallback(
    (community) => setActiveCommunity(community),
    [setActiveCommunity]
  );

  const showSideBar = useMemo(() => {
    return (
      !isInFrame &&
      ![
        // '/portal/events/add',
        '/portal/events/edit/[eventId]',
        '/portal/settings/payout-form',
        '/portal/library/[folderId]',
        '/portal/challenges/[challengeId]',
        '/portal/products/[folderId]',
        '/portal/magic-reach/new',
        '/portal/products/1on1session/manage/[productId]'
      ].includes(pathName)
    );
  }, [pathName]);

  useEffect(() => {
    if (isDemoUser && !isSessionInSandboxEnv()) {
      handleLogout();
    }
  }, [isDemoUser]);

  useEffect(() => {
    if (config.whatsappDisabling) {
      setIsAnnouncementBannerOpened(
        activeCommunity?.isWhatsappExperienceCommunity
      );
    }
  }, [activeCommunity?.isWhatsappExperienceCommunity]);

  const getFaviconUrl = () => {
    const imageUrl = activeCommunity?.thumbnailImgData?.mobileImgData?.src;

    const favIconUrl =
      config.isImageOptimizationEnabled &&
      checkIfNewImageOptimization(imageUrl)
        ? generateImagePath({
            src: imageUrl,
            quality: 25,
            isThumbnail: true
          })
        : config.siteBaseUrl +
          generateNextImagePath({
            src: imageUrl,
            quality: 25,
            w: 64
          });

    return favIconUrl;
  };

  const communityMetaTag = {
    title: `${NASIO_MANAGER_PORTAL_TEXT} - ${activeCommunity?.title}`,
    description: activeCommunity?.description,
    faviconUrl: getFaviconUrl()
  };

  return (
    <div className="flex flex-col overflow-hidden min-h-0 h-[100dvh]">
      {activeCommunity && <PageMetaTags {...communityMetaTag} />}
      <div className={`${className} w-full font-poppins lg:mx-auto`}>
        {isGtEqLg && !isInFrame && (
          <>
            {activeCommunity?.restrictedInfo?.checkout && (
              <KycRequiredBanner activeCommunity={activeCommunity} />
            )}
            <div className="h-64">
              <div className="w-full h-64 bg-white-default px-12 py-12">
                <AuthNavBar
                  communityInfo={activeCommunity}
                  userCommunities={allCommunities}
                  isPortalView
                  setActiveCommunity={selectActiveCommunity}
                  isCommunityAdmin={true}
                />
              </div>
            </div>
            {isAnnouncementBannerOpened &&
              pathName.includes('magic-reach') && (
                <AnnouncementBar
                  ctaText={t('see-more')}
                  title="WhatsApp DM is currently not available due to an issue on the WhatsApp platform"
                  bannerClassName="bg-npl-yellow-light-solid-3"
                  onClick={() => {
                    openInNewTab(config.whatsappUnavailabilityLink);
                  }}
                  close={() => {
                    setIsAnnouncementBannerOpened(false);
                  }}></AnnouncementBar>
              )}
          </>
        )}
      </div>
      {!loadingCommunities && allCommunities?.length > 0 && (
        <div className="blur-y-0 flex w-full flex-col min-h-0 flex-1">
          <div
            className={`${className} flex w-full flex-col lg:m-auto lg:flex-row min-h-0 flex-1`}>
            {isGtEqLg && showSideBar && (
              <aside className="w-[240px] h-full overflow-y-auto">
                <PortalSidebar
                  user={user}
                  pageItems={getNavigationItems()}
                />
              </aside>
            )}

            {showDrawerNav && (
              <>
                <DrawerNavbar
                  user={user}
                  activeCommunity={activeCommunity}
                  communityList={communityNavigationList}
                  navigationItems={getNavigationItems()}
                  handleLogout={handleLogout}
                />

                {isAnnouncementBannerOpened &&
                  pathName.includes('magic-reach') && (
                    <AnnouncementBar
                      ctaText={t('see-more')}
                      title={t(
                        'whatsapp-dm-is-currently-not-available-due-to-an-issue-on-the-whatsapp-platform'
                      )}
                      bannerClassName={'bg-npl-yellow-light-solid-3'}
                      onClick={() => {
                        openInNewTab(config.whatsappUnavailabilityLink);
                      }}
                      close={() => {
                        setIsAnnouncementBannerOpened(false);
                      }}></AnnouncementBar>
                  )}
              </>
            )}

            {/*Main content goes here*/}
            <div
              className={classNames(
                `${className}__main_page relative right-0 z-0 min-w-40 h-full flex-1 transition-all duration-[400ms!important] overflow-auto flex flex-col min-h-0`
              )}>
              {children}
            </div>
          </div>
        </div>
      )}

      {loadingCommunities && (
        <div className="flex h-[calc(100vh-64px)] w-screen flex-col items-center justify-center">
          <LoadingCircle />
          <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-secondary">
            {t('loading-your-community')}
          </div>
        </div>
      )}

      {communitiesErr && (
        <div className="mx-auto mt-[120px] w-[380px]">
          <Card error={communitiesErr} center>
            {communitiesErr}
          </Card>
        </div>
      )}

      {showConnectWhatsAppModals && (
        <WaConnectionModals
          showModals={showConnectWhatsAppModals}
          closeModals={handleCloseWhatsappConnectModals}
        />
      )}

      {isAnnouncementModalOpen && <AnnouncementModal {...modalProps} />}

      {activeCommunity && (
        <CampaignAnnouncementModals activeCommunity={activeCommunity} />
      )}

      {config.isWelcomeMessageAvailable && isWelcomeMessageEditorOpen && (
        <EditWelcomeMessageSideDrawer
          open={isWelcomeMessageEditorOpen}
          onClose={() => setIsWelcomeMessageEditorOpen(false)}
          communityId={activeCommunity?._id}
          isDemoCommunity={activeCommunity?.isDemo}
          communityType={{
            whatsAppCommunity:
              activeCommunity?.isWhatsappExperienceCommunity,
            requireApproval: activeCommunity?.request_approval
          }}
        />
      )}

      {/* Only show if user does not have any community */}
      {isNoCommunityModalOpen && <NoCommunityModal />}

      {isGetStartedDismissalModalOpen && (
        <GetStartedDismissalModal
          onClose={() => setIsGetStartedDismissalModalOpen(false)}
        />
      )}
    </div>
  );
};

export default PortalLayout;
